import React from "react";
import { motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "What kind of jobs can I post on Raanah?",
    a: "Anything that fits a Luton van. House removals (studio to 4-bed), waste clearance (garden, builders, garage), house clearances, office moves, single-item courier work, and self-storage transfers. We're focused on the UK market and high-value local jobs.",
  },
  {
    q: "How does the escrow actually work?",
    a: "When a customer accepts a quote, Raanah charges their card and holds the full amount with Stripe Connect. Funds are released to the driver only after proof of completion is uploaded and verified. Commission is deducted at that point and queued for the Friday payout.",
  },
  {
    q: "Do drivers need to install an app?",
    a: "No. Drivers run entirely on WhatsApp — they get job alerts, accept jobs, upload proof-of-completion photos, and receive payout confirmations all through chat. A web dashboard exists for those who want it, but it's optional.",
  },
  {
    q: "How much can a self-employed Luton van driver actually earn?",
    a: "Active Driver Pro members on the platform target £600–£1,200/day during busy weekends doing 2–3 house moves. Weekday clearance work typically pays £200–£500 per job. Earnings depend on your area, availability, and rating — but the model is built for high earners, not bottom-of-the-barrel courier work.",
  },
  {
    q: "What happens during a dispute?",
    a: "Funds remain in escrow. Both parties can submit evidence (photos, GPS, signed completion notes). The Raanah admin team mediates within 48 hours and releases or refunds based on the audit trail. Repeat bad actors are removed from the platform.",
  },
  {
    q: "How is commission calculated?",
    a: "Commission is a percentage of each completed job — 6% on Driver Pro, 12% on the free tier. Customers pay a 5% service fee on top of the driver's quote. No hidden line items, all charges visible upfront.",
  },
  {
    q: "When do drivers actually get paid?",
    a: "Every Friday, automatically. Earnings accrue in real time in the driver dashboard. Driver Pro members can request same-day payout (small fee) at any point.",
  },
  {
    q: "Is Raanah live right now?",
    a: "We're in pilot soft-launch mode and onboarding a small set of verified Luton van drivers and customers across Leeds, Manchester, Birmingham, and London. Join the waitlist below for early access.",
  },
];

export default function FAQ() {
  const [open, setOpen] = React.useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-accordion"
      className="bg-[#F8FAFC] border-y border-[#0A0A0A]/5"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              FAQ
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
              Questions, answered.
            </h2>
            <p className="mt-5 text-[#475569]">
              Can't find what you're looking for? Reach us at{" "}
              <a
                href="mailto:hello@raanah.com"
                data-testid="faq-contact-link"
                className="text-[#0A0A0A] underline underline-offset-4 hover:text-[#1E4D55]"
              >
                hello@raanah.com
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 border-t border-[#0A0A0A]/10">
            {faqs.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-[#0A0A0A]/10"
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-center justify-between text-left py-5 group"
                    aria-expanded={isOpen}
                  >
                    <span className="font-display text-lg md:text-xl font-bold tracking-tight text-[#0A0A0A] group-hover:text-[#1E4D55] transition-colors pr-6">
                      {item.q}
                    </span>
                    <span className="shrink-0 w-9 h-9 border border-[#0A0A0A]/15 flex items-center justify-center text-[#0A0A0A]">
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: isOpen ? "auto" : 0,
                      opacity: isOpen ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 pr-12 text-[#475569] leading-relaxed">
                      {item.a}
                    </p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
